import { ElementType, ReactNode } from 'react';

import { makeStyles } from '@material-ui/core/styles';

import classNames from 'classnames';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--bui-space-2)',
    paddingInline: 'var(--bui-space-2)',
    paddingBlock: 'var(--bui-space-2)',
    color: 'var(--bui-fg-primary)',
    fontSize: 'var(--bui-font-size-3)',
    textDecoration: 'none',
    width: '100%',
    minWidth: 0,
  },
  interactive: {
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    textAlign: 'inherit',
    font: 'inherit',
    borderRadius: 'var(--bui-radius-2)',
    '&:hover': {
      backgroundColor: 'var(--bui-bg-surface-2)',
    },
    '&:focus-visible': {
      outline: '2px solid var(--bui-ring)',
      outlineOffset: '-2px',
    },
    ':where([data-bg="neutral-1"]) &:hover': {
      backgroundColor: 'var(--bui-bg-neutral-2)',
    },
    ':where([data-bg="neutral-2"]) &:hover': {
      backgroundColor: 'var(--bui-bg-neutral-3)',
    },
    ':where([data-bg="neutral-3"]) &:hover': {
      backgroundColor: 'var(--bui-bg-neutral-4)',
    },
  },
});

const INTERACTIVE_ELEMENTS = new Set(['button', 'a']);

export type ListItemRootProps<E extends ElementType = 'div'> = {
  component?: E;
  className?: string;
  children?: ReactNode;
  interactive?: boolean;
} & Omit<React.ComponentPropsWithoutRef<E>, 'className' | 'children'>;

export const ListItemRoot = <E extends ElementType = 'div'>(
  props: ListItemRootProps<E>,
) => {
  const { component, className, children, interactive, ...rest } = props;
  const classes = useStyles();

  const Component: ElementType = component || 'div';
  const isNativeInteractive =
    typeof Component === 'string' && INTERACTIVE_ELEMENTS.has(Component);
  const isCustomComponent =
    typeof Component !== 'string' && Component !== undefined;
  const isInteractive =
    interactive ?? (isNativeInteractive || isCustomComponent);

  return (
    <Component
      className={classNames(
        classes.root,
        isInteractive && classes.interactive,
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
};

ListItemRoot.displayName = 'ListItem.Root';
